import { useEffect, useState } from 'react';
import { useSettings } from './hooks/useSettings.js';
import { useSpots } from './hooks/useSpots.js';
import { useFilteredSpots } from './hooks/useFilteredSpots.js';
import Header from './components/Header.jsx';
import Toolbar from './components/Toolbar.jsx';
import Map from './components/Map.jsx';
import Table from './components/Table.jsx';
import Drawer from './components/Drawer.jsx';
import SettingsPanel from './components/SettingsPanel.jsx';
import BottomTabs from './components/BottomTabs.jsx';
import SwipeView from './components/SwipeView.jsx';

export default function App() {
  const [settings, updateSettings, resetSettings, ioPort] = useSettings();
  const { spots, locators, status } = useSpots({ limit: settings.rowLimit });
  const { filtered, alertIds } = useFilteredSpots(spots, settings);
  const [selectedId, setSelectedId] = useState(null);
  const [hoverId, setHoverId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [tab, setTab] = useState('map'); // mobile-only: 'map' | 'table'

  // Apply the selected theme as a data-attribute on the html element so
  // index.css `[data-theme="..."]` overrides take effect globally.
  useEffect(() => {
    document.documentElement.dataset.theme = settings.theme || 'dark';
  }, [settings.theme]);

  return (
    <div className="h-dvh flex flex-col bg-slate-950 text-slate-100">
      <Header
        status={status}
        onOpenDrawer={() => setDrawerOpen(true)}
      />
      <Toolbar settings={settings} onUpdate={updateSettings} />

      {/* Main content area:
          mobile (< md): SwipeView carousel — Map and Table in a swipeable track
          desktop (≥ md): SwipeView is inert; both panes render side-by-side */}
      <main className="flex-1 flex overflow-hidden">
        <SwipeView tab={tab} onChange={setTab} tabs={['map', 'table']}>
          <div className="flex-1 min-w-0">
            <Map
              spots={filtered}
              locators={locators}
              settings={settings}
              alertIds={alertIds}
              selectedId={selectedId}
              onSelect={setSelectedId}
              hoverId={hoverId}
              onHover={setHoverId}
              onUpdate={updateSettings}
            />
          </div>
          <div className="md:w-[480px] lg:w-[560px] xl:w-[640px] 2xl:w-[768px]
                          md:border-l md:border-slate-800
                          flex-1 md:flex-initial md:flex-shrink-0 overflow-hidden min-w-0">
            <Table
              spots={filtered}
              alertIds={alertIds}
              selectedId={selectedId}
              onSelect={setSelectedId}
              hoverId={hoverId}
              onHover={setHoverId}
            />
          </div>
        </SwipeView>
      </main>

      <BottomTabs tab={tab} onChange={setTab} />

      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <SettingsPanel
          settings={settings}
          onUpdate={updateSettings}
          onReset={resetSettings}
          ioPort={ioPort}
        />
      </Drawer>
    </div>
  );
}
